// src/components/IndexPage.ts
import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { AuthContext, AuthState } from './AuthProvider';
import AppAnalytics from '../client/AnalyticsClient';
import { getDiffsonaPrompts, getGeneratedContent } from '../client/ApiClient';
import { DiffsonaPrompts } from './CurrentDiffsona';
import { Job, PromptPayload } from './CreatePage';
import { CDN_CONTENT_ENDPOINT } from '../constants';

@customElement('character-index')
export class CharacterIndex extends LitElement {
  createRenderRoot() { return this; }

  @consume({ context: AuthContext, subscribe: true })
  @state() authState!: AuthState;

  @state() private recentSlug: string | null = null;
  @state() private hasAny = false;
  @state() private newOpen = false;
  @state() private checkingDiffsonas = false;

  @state() private demoPreset: string | null = null;
  @state() private selectedDiffsona: { slug: string; name: string; prompts: DiffsonaPrompts; jobs: Job[] } | null = null;
  @state() private loadingPrompts = false;
  @state() private showCreateDemo = false;

  connectedCallback(): void {
    super.connectedCallback();
    // Parse demo-preset search parameter
    const urlParams = new URLSearchParams(window.location.search);
    this.demoPreset = urlParams.get('dp');
  }

  firstUpdated() {
    requestAnimationFrame(() => this.shadowRoot?.querySelector('#root')?.classList.add('ready'));
    try {
      const v = localStorage.getItem('recent:diffsona:slug');
      this.recentSlug = v ? JSON.parse(v) : null;
    } catch { this.recentSlug = null; }

    this.addEventListener('my-diffsonas-loaded', (e: any) => {
      const count = Number(e.detail?.count || 0);
      const items = e.detail?.items || [];
      this.hasAny = count > 0;
      this.checkingDiffsonas = false;

      // Auto-select first diffsona if available
      if (items.length > 0 && !this.selectedDiffsona) {
        const first = items[0];
        this.selectDiffsona(first.slug, first.name);
      }
    });

    this.addEventListener('diffsona-selected', async (e: any) => {
      const { slug, name } = e.detail;
      this.selectDiffsona(slug, name);
    });

    this.addEventListener('create-new-diffsona', () => {
      this.selectedDiffsona = null;
      this.showCreateDemo = true;
    });

    this.addEventListener('scroll-to-create', () => {
      // Wait for render to complete, then scroll to create section
      requestAnimationFrame(() => {
        const createSection = this.querySelector('diffsona-create-page');
        if (createSection) {
          createSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);

    // Trigger diffsona check when auth state becomes available
    if (changedProperties.has('authState')) {
      const isLoggedIn = this.authState?.user !== null && this.authState?.user !== undefined;
      if (isLoggedIn && !this.checkingDiffsonas) {
        this.checkingDiffsonas = true;
      }
    }
  }

  private async loadGeneratedContent(slug: string): Promise<Job[]> {
    try {
      const result = await getGeneratedContent(slug);
      // Convert relative image paths to full CDN URLs and map to Job format
      return result.image_urls.map((relativeUrl, idx) => ({
        jobId: `${slug}-${idx}`,
        status: 'done' as const,
        startedAt: Date.now() - 1000000, // Placeholder timestamp
        elapsedSec: 0,
        imageUrl: `${CDN_CONTENT_ENDPOINT}${relativeUrl}`,
        summary: '',
        prompt: {} as PromptPayload,
      }));
    } catch (err) {
      console.error('Failed to load generated content:', err);
      return [];
    }
  }

  private async selectDiffsona(slug: string, name: string) {
    this.loadingPrompts = true;
    try {
      const [prompts, jobs] = await Promise.all([
        getDiffsonaPrompts(slug),
        this.loadGeneratedContent(slug)
      ]);
      this.selectedDiffsona = {
        slug,
        name,
        prompts: {
          name: prompts.name,
          base_prompt: prompts.base_prompt,
          age_prompt: prompts.age_prompt,
          ethnicity_prompt: prompts.ethnicity_prompt,
          hair_prompt: prompts.hair_prompt,
          face_prompt: prompts.face_prompt,
          body_prompt: prompts.body_prompt,
          bust_prompt: prompts.bust_prompt,
          waist_prompt: prompts.waist_prompt,
          hips_prompt: prompts.hips_prompt,
        },
        jobs
      };
    } catch (err) {
      console.error('Failed to load diffsona:', err);
    } finally {
      this.loadingPrompts = false;
    }
  }

  private openLogin() {
    AppAnalytics.getPlausible()?.trackEvent('auth_open_login');
    this.dispatchEvent(new CustomEvent('auth-required', { bubbles: true, composed: true }));
  }

  render() {
    const loggedOut = !this.authState?.user;
    const isLoggedIn = this.authState?.user !== null && this.authState?.user !== undefined;

    // Curated demo thumbnails for immediate credibility
    const demoImages: string[] = [
      //      '/static/demo/looks/001.webp',
    ];

    return html`
      <main id="root" class="min-h-[90vh] px-2">
        ${loggedOut ? html`
          <!-- Logged out: Show demo with promo banner -->
          <section class="max-w-7xl mx-auto pt-1">
            <div class="relative rounded-2xl border border-white/20 bg-gradient-to-br from-slate-900/90 via-indigo-950/30 to-slate-950/90
                        backdrop-blur shadow-lg shadow-indigo-500/10 px-2 py-1 text-center group
                        hover:shadow-xl hover:shadow-indigo-500/20 transition-all duration-500 ease-out
                        hover:border-indigo-400/30">

              <!-- Subtle sparkle in top corner -->
              <div class="absolute top-2 right-3 w-1.5 h-1.5 bg-indigo-300 rounded-full animate-pulse opacity-60"></div>

              <p class="text-slate-200 flex gap-1 justify-center items-center">
                <span class="group-hover:text-indigo-100 transition-colors duration-300 text-sm">
                  ✨ Start free with
                </span>
                <span class="text-emerald-400 font-bold flex items-center justify-center gap-1.5
                           group-hover:text-emerald-300 group-hover:scale-105 transition-all duration-300">
                  1200 <img src="/static/images/diffso-coin-64.webp" alt="Credits" class="h-7 w-7 group-hover:animate-pulse" />
                </span>
                <span class="hidden md:inline text-slate-400 group-hover:text-slate-300 transition-colors duration-300">—</span>
              </p>

              <button class="mt-1 px-4 py-1 rounded-xl border border-indigo-400/40 bg-indigo-500/20
                            hover:bg-indigo-500/30 hover:border-indigo-400/60 hover:scale-105
                            text-indigo-200 hover:text-white font-medium
                            transition-all duration-300 ease-out
                            active:scale-95"
                      @click=${() => this.openLogin()}>
                <span class="flex items-center gap-1.5 text-sm">
                  Claim Free Credits
                  <span class="text-xs opacity-70">→</span>
                </span>
              </button>
            </div>
          </section>

          <!-- FRONT & CENTER: the actual builder in demo mode, prefilled -->
          <section class="w-full max-w-7xl mx-auto pt-1"
                   @auth-required=${() => this.openLogin()}>
            <diffsona-create-page
              slug=""
              .demo=${true}
              .prefillOnLoad=${true}
              .demoImages=${demoImages}
              demo-preset=${this.demoPreset || ''}
            ></diffsona-create-page>
          </section>
        ` : isLoggedIn ? html`
          <!-- Logged-in: Show my-diffsonas in select mode -->
          <section class="w-full max-w-7xl mx-auto pt-2">
            <my-diffsonas mode="select" .selectedSlug=${this.selectedDiffsona?.slug || ''}></my-diffsonas>
          </section>

          <!-- Show create page in demo mode or with selected diffsona -->
          ${this.showCreateDemo ? html`
            <section class="w-full max-w-7xl mx-auto pt-2">
              <diffsona-create-page
                slug=""
                .demo=${true}
                .prefillOnLoad=${true}
              ></diffsona-create-page>
            </section>
          ` : this.selectedDiffsona ? html`
            <section class="w-full max-w-7xl mx-auto pt-2">
              <diffsona-create-page
                .slug=${this.selectedDiffsona.slug}
                .diffsonaPrompts=${this.selectedDiffsona.prompts}
                .initialJobs=${this.selectedDiffsona.jobs}
              ></diffsona-create-page>
            </section>
          ` : this.loadingPrompts ? html`
            <section class="w-full max-w-7xl mx-auto pt-2">
              <div class="rounded-2xl border border-white/10 bg-slate-900/40 p-8 text-center">
                <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
                <div class="mt-4 text-slate-300">Loading diffsona...</div>
              </div>
            </section>
          ` : null}
        ` : null}
      </main>
    `;
  }
}

